import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import { ArrowLeft, Play, Sparkles, Loader2 } from "lucide-react";
import { stakeUrl } from "../lib/stake";

export default function GameDetail() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [game, setGame] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/games/${slug}`).then((r) => setGame(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const frame = (inner, navEl = <TopNav />) => user
    ? <AppShell>{inner}</AppShell>
    : <div className="relative min-h-screen text-white">{navEl}{inner}</div>;

  if (loading && !game && !notFound) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  if (notFound) {
    return frame(
      <div className={`${user ? "pt-16" : "pt-40"} text-center`}>
        <div className="text-[13px] text-white/40">Not found</div>
        <div className="mt-2 text-3xl font-bold">This game doesn&apos;t exist.</div>
        <Link to="/" className="mt-6 inline-block text-white/70 hover:text-white">Back home</Link>
      </div>
    );
  }

  if (!game) {
    return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;
  }

  return frame(
    <div className="relative min-h-screen text-white">
      <SEO
        title={`${game.title} — ${game.tagline}`}
        description={`${game.title}: ${game.genre} by Gumble Studio. RTP ${game.rtp}, max win ${game.max_win}, ${game.volatility} volatility. Play exclusive on Stake.`}
        path={`/games/${game.slug}`}
        image={game.cover}
      />
      {/* Hero backdrop */}
      <div className={`relative ${user ? "pt-10 md:pt-14" : "pt-32"} pb-16 px-6 md:px-10`}>
        <div className="max-w-6xl mx-auto">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-[13px] text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</button>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-3">
              <div className="relative rounded-[28px] overflow-hidden aspect-[16/10] glass">
                <img src={game.backdrop || game.cover} alt={`${game.title} — key art`} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="text-[12px] tracking-[0.25em] text-white/50">{game.genre}</div>
                  <h1 className="mt-2 text-5xl md:text-7xl font-black tracking-[-0.03em]">{game.title}</h1>
                  <div className="mt-2 text-[15px] text-white/70">{game.tagline}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="glass rounded-3xl p-6">
                <div className="text-[12px] uppercase tracking-widest text-white/50">Overview</div>
                <p className="mt-3 text-[15px] leading-relaxed text-white/80">{game.description}</p>
              </div>
              <div className="glass rounded-3xl p-6 grid grid-cols-2 gap-4">
                <Stat k="RTP" v={game.rtp} />
                <Stat k="Max win" v={game.max_win} accent />
                <Stat k="Volatility" v={game.volatility} />
                <Stat k="Layout" v={game.reels} />
              </div>
              <a href={stakeUrl(game.slug)} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-4 rounded-full bg-[#0A84FF] text-white text-[15px] font-semibold hover:bg-[#0077ED] transition-colors">
                <Play className="w-4 h-4 fill-white" /> Play on Stake
              </a>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-[12px] uppercase tracking-widest text-white/50 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-white/60" /> Features</h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {(game.features || []).map((f) => (
                <div key={f} className="glass rounded-2xl px-4 py-4 text-[14px] text-white/85">{f}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-white/40">{k}</div>
      <div className={`mt-1 text-[17px] font-semibold text-white`}>{v}</div>
    </div>
  );
}
